import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Gift, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney } from "@/lib/api";
import { inputCls, btnSecondary, cardCls, CheckCircle } from "@/components/shared";

export const LinkedShopping = ({ list, shops }) => {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const linked = shops.data.flatMap((s) => s.items.filter((i) => i.linked_todo_list_id === list.id).map((i) => ({ ...i, shop: s })));
  const total = linked.filter((i) => !i.bought).reduce((s, i) => s + (i.price || 0), 0);

  const toggle = async (it) => shops.replace((await api.patch(`/shop-lists/${it.shop.id}/items/${it.id}`, { bought: !it.bought })).data);
  const quickAdd = async (e) => {
    e.preventDefault();
    const shopId = target || shops.data[0]?.id;
    if (!name.trim() || !shopId) return;
    shops.replace((await api.post(`/shop-lists/${shopId}/items`, { name: name.trim(), linked_todo_list_id: list.id })).data);
    setName("");
    toast.success("Linked item added");
  };

  return (
    <div className={`${cardCls} p-6 sm:p-8 bg-t-surface/70 fade-up`} data-testid="linked-shopping-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-t-c2">Linked</p>
          <h3 className="font-serif-display text-2xl font-semibold">Things to buy for this list</h3>
        </div>
        <span className="text-sm font-medium text-t-c4" data-testid="linked-shopping-total">{fmtMoney(total)} left</span>
      </div>
      {linked.length ? (
        <ul className="space-y-2 mb-5">
          {linked.map((it) => (
            <li key={it.id} data-testid={`linked-item-${it.id}`} className="flex items-center gap-3 bg-t-card rounded-xl px-4 py-3 border border-t-line">
              <CheckCircle checked={it.bought} onClick={() => toggle(it)} color="rgb(var(--t-c2))" testId={`linked-item-check-${it.id}`} />
              {it.shop.kind === "wishlist" ? <Gift className="h-4 w-4 text-t-c5" /> : <ShoppingBag className="h-4 w-4 text-t-c2" />}
              <span className={`flex-1 text-sm ${it.bought ? "line-through text-t-faint" : ""}`}>{it.name}</span>
              <span className="text-xs text-t-muted">{it.shop.name}</span>
              {it.price != null && <span className="text-sm font-medium">{fmtMoney(it.price)}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-t-muted mb-5" data-testid="linked-shopping-empty">No wishlist or to-buy items linked yet.</p>
      )}
      {shops.data.length ? (
        <form onSubmit={quickAdd} className="flex flex-col sm:flex-row gap-3" data-testid="linked-quick-add-form">
          <input data-testid="linked-quick-add-input" className={`${inputCls} flex-1`} placeholder="Something you need for this..." value={name} onChange={(e) => setName(e.target.value)} />
          <select data-testid="linked-quick-add-select" className={`${inputCls} sm:w-48`} value={target} onChange={(e) => setTarget(e.target.value)}>
            {shops.data.map((s) => (
              <option key={s.id} value={s.id}>{`${s.name} (${s.kind === "wishlist" ? "Wishlist" : "To-Buy"})`}</option>
            ))}
          </select>
          <button type="submit" data-testid="linked-quick-add-button" className={btnSecondary}><Plus className="h-4 w-4" /> Link</button>
        </form>
      ) : (
        <Link to="/lists" data-testid="go-create-shop-list-link" className="text-sm text-t-primary font-medium hover:underline">
          Create a wishlist or to-buy list first →
        </Link>
      )}
    </div>
  );
};
